import { Action, createReducer, on } from "@ngrx/store";
import * as authActions from './auth.actions';
import { AppError } from "src/app/core/models/api-error";
import { MfaChallenge } from "../models/mfa-challenge";

export interface AuthState {
  loading: boolean
  /** The last login failure, or null. Held in state rather than left to a
   *  toast so it survives long enough to be read and acted on. */
  error: AppError | null
  /** The open second-factor challenge. State only -- never localStorage,
   *  never a Bearer. A reload drops it, and should. */
  challenge: MfaChallenge | null
  /** ISO timestamp before which an email-code resend is refused, or null. */
  emailCodeNextAllowedAt: string | null
  /** The address the last login code went to, masked by the server. */
  emailCodeMaskedAddress: string | null
  /** A send is in flight. The throttle only starts once the reply lands, so
   *  a second click would otherwise get through. */
  emailCodeSending: boolean
  /** Where the stored `error` came from, so the challenge screen only clears
   *  the field for a refused code. */
  errorSource: 'auth' | 'email_code' | null
}

export const initialAuthState: AuthState = {
  loading: false,
  error: null,
  challenge: null,
  emailCodeNextAllowedAt: null,
  emailCodeMaskedAddress: null,
  emailCodeSending: false,
  errorSource: null,
}

const featureReducer = createReducer(
  initialAuthState,
  on(authActions.SetAuthLoading, (state, action) => ({
    ...state,
    loading: action.isLoading
  })),
  // Cleared on the attempt, not on success: a retry must not sit under the
  // previous banner, or under a challenge it has abandoned.
  on(authActions.LoginAction, (state) => ({
    ...state,
    error: null,
    errorSource: null,
    challenge: null,
    emailCodeNextAllowedAt: null,
    emailCodeMaskedAddress: null,
    emailCodeSending: false
  })),
  on(authActions.LoginActionError, (state, action) => ({
    ...state,
    error: action.error,
    errorSource: 'auth' as const
  })),
  on(authActions.MfaChallengeRequired, (state, action) => ({
    ...state,
    error: null,
    errorSource: null,
    challenge: action.challenge
  })),
  // The previous refusal must not sit under a code in flight.
  on(authActions.MfaVerifyAction, (state) => ({
    ...state,
    error: null,
    errorSource: null
  })),
  on(authActions.MfaSendEmailCode, (state) => ({
    ...state,
    error: null,
    errorSource: null,
    emailCodeSending: true
  })),
  on(authActions.MfaEmailCodeSent, (state, action) => ({
    ...state,
    emailCodeNextAllowedAt: action.nextAllowedAt,
    emailCodeMaskedAddress: action.maskedEmail,
    emailCodeSending: false
  })),
  on(authActions.MfaSendEmailCodeError, (state, action) => ({
    ...state,
    error: action.error,
    errorSource: 'email_code' as const,
    emailCodeSending: false
  })),
  // Drops the challenge only; MfaChallengeExpiredEffect$ localizes the
  // message and dispatches it right behind this one.
  on(authActions.MfaChallengeExpired, (state) => ({
    ...state,
    error: null,
    errorSource: null,
    challenge: null,
    emailCodeNextAllowedAt: null,
    emailCodeMaskedAddress: null,
    emailCodeSending: false
  })),
  on(authActions.MfaChallengeCleared, (state) => ({
    ...state,
    error: null,
    errorSource: null,
    challenge: null,
    emailCodeNextAllowedAt: null,
    emailCodeMaskedAddress: null,
    emailCodeSending: false
  })),
  // Nothing about the challenge outlives the session it produced.
  on(authActions.LoginActionSuccess, (state) => ({
    ...state,
    error: null,
    errorSource: null,
    challenge: null,
    emailCodeNextAllowedAt: null,
    emailCodeMaskedAddress: null,
    emailCodeSending: false
  }))
);

export function authReducer(state: AuthState | undefined, action: Action) {
  return featureReducer(state, action);
}
